import {
    selectQuestionGroupMaxTotalScore,
    selectQuestionGroupName,
    selectIsFormWeighted,
    toggleFormWeightMode,
    selectCanEditForm,
    selectTotalGroupsPoints,
    selectQuestionGroupMaxTotalWeightedScore,
    selectTotalGroupsScaledPoints,
} from "features/qualityForm/qualityFormSlice";
import { toGuxDisabled } from "util/componentUtil";
import {
    selectForm,
    selectFormType,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

import { GuxToggle, GuxTruncate } from "genesys-spark-components-react";
import { PercentageBar } from "./PercentageBar";
import "./Scoring.less";
import strings from "./Scoring.strings";
import { useStrings } from "features/i18n/useStrings";
import { ScoreTag } from "features/common/ScoreTag";
import { I18nComponent } from "react/utils/I18nComponent";
import FormType from "enums/FormType";
import {
    GroupScoreScalingFactorViewer,
    GroupScoreScalingFactorInput,
} from "../questionGroup/QuestionGroupWeights";
import { LevelIndicator } from "features/qualityForm/common/LevelIndicator";
import {
    LevelContext,
    PreviewModeContext,
} from "features/qualityForm/common/Contexts";
import { useContext } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { Card } from "features/common/Card";
import PassPercent from "../PassPercent";
import { Decimal } from "features/i18n/Percentage";
import { MarkdownInline } from "features/common/Markdown";

/**
 *
 * @param totalPoints - by DefaultLimits.maxQuestionCount (i.e. 200) and max Question Point value (i.e. 2,147,483,647), it is expected to be less than 429,496,729,400
 * @param totalScaledPoints - with totalPoints and DefaultLimits.maxQuestionGroupScalingFactor (i.e. 999,999), it is expected to be less than 4.294963e+17
 *
 * [Ideal/Reasonable Case] the max value for total scaled points could be between 10,000,000 and 100,000,000 (which fits in the correct positions with no overflow in all the breakpoints)
 *
 * [Extreme Case] At the browser with width 768 px, WHEN totalPoints is above "4.29e+28" and Group Scaling Factor is 999,999 (max),
 * the UI starts to get broken with uneven padding and overflowing the content box on the side panel.
 *
 */
export function TotalPoints({
    totalPoints,
    totalScaledPoints,
}: {
    totalPoints: number;
    totalScaledPoints?: number;
}) {
    return (
        <div className="TotalPoints">
            <div className="total-points gux-label">
                <I18nComponent
                    i18nKey="Scoring.labels.Points"
                    substitutions={{
                        ScoreTag: <ScoreTag>{totalPoints}</ScoreTag>,
                    }}
                />
            </div>
            {totalScaledPoints !== undefined && (
                <div className="total-scaled-points gux-label">
                    <I18nComponent
                        i18nKey="Scoring.labels.ScaledPoints"
                        substitutions={{
                            ScoreTag: (
                                <ScoreTag>
                                    <Decimal maxDigits={3}>
                                        {totalScaledPoints}
                                    </Decimal>
                                </ScoreTag>
                            ),
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export function Scoring() {
    const { previewMode } = useContext(PreviewModeContext);

    const questionGroups =
        useAppSelector((state) => selectForm(state))?.questionGroups ?? [];
    const totalPoints =
        useAppSelector((state) => selectTotalGroupsPoints(state)) ?? 0;
    const totalScaledPoints =
        useAppSelector((state) => selectTotalGroupsScaledPoints(state)) ?? 0;

    const disableSidePanelEditing = useAppSelector((state) => {
        return !selectCanEditForm(state) || previewMode;
    });

    const formType = useAppSelector(selectFormType);
    const isWeighted = useAppSelector(selectIsFormWeighted);

    const dispatch = useAppDispatch();

    const questionGroupList = questionGroups.map((groupId, index) => {
        return (
            <li key={groupId}>
                <LevelContext.Provider
                    value={{ level: index + 1, subLevel: 0 }}
                >
                    <Card>
                        <GroupScore
                            totalPoints={totalPoints}
                            totalScaledPoints={totalScaledPoints}
                            groupId={groupId}
                        />
                    </Card>
                </LevelContext.Provider>
            </li>
        );
    });
    const t = useStrings(strings);

    return (
        <div className="ScoringPanel">
            {formType === FormType.EVALUATION && (
                <GuxToggle
                    className={`weighted-scoring-feature-toggle ${
                        !disableSidePanelEditing ? "Clickable" : ""
                    }`}
                    checkedLabel={t.labels.FlipSwitch.GroupScaledScoringOn}
                    uncheckedLabel={t.labels.FlipSwitch.GroupScaledScoringOff}
                    checked={isWeighted}
                    onCheck={() => dispatch(toggleFormWeightMode())}
                    disabled={toGuxDisabled(disableSidePanelEditing)}
                    aria-disabled={toGuxDisabled(disableSidePanelEditing)}
                    data-testid="scaled-scoring-toggle"
                />
            )}
            {formType === FormType.ASSESSMENT && (
                <PassPercent disabled={disableSidePanelEditing} />
            )}
            <TotalPoints
                totalPoints={totalPoints}
                totalScaledPoints={isWeighted ? totalScaledPoints : undefined}
            />
            <ul className="question-group-list-view">{questionGroupList}</ul>
        </div>
    );
}

export function GroupScore({
    totalPoints,
    totalScaledPoints,
    groupId,
}: {
    totalPoints: number;
    totalScaledPoints: number;
    groupId: string;
}) {
    const questionGroupTotalPoints =
        useAppSelector((state) =>
            selectQuestionGroupMaxTotalScore(state, groupId)
        ) ?? 0;

    const scaledPoints = useAppSelector((state) =>
        selectQuestionGroupMaxTotalWeightedScore(state, groupId)
    );

    const groupName =
        useAppSelector((state) => selectQuestionGroupName(state, groupId)) ??
        "";

    const canEditForm = useAppSelector(selectCanEditForm);
    const isWeighted = useAppSelector(selectIsFormWeighted);

    const percentage = isWeighted
        ? totalScaledPoints != 0
            ? (Number(scaledPoints) * 100) / totalScaledPoints
            : 0
        : totalPoints != 0
        ? (Number(questionGroupTotalPoints) * 100) / totalPoints
        : 0;
    const t = useStrings(strings);

    return (
        <div className="GroupScore">
            <div className="title">
                <LevelIndicator variant="circle" />
                <div className="gux-heading-xs-semibold name">
                    <GuxTruncate max-lines={3}>
                        <MarkdownInline
                            markdownInlineText={groupName ?? ""}
                        ></MarkdownInline>
                    </GuxTruncate>
                </div>
            </div>

            <TotalPoints
                totalPoints={questionGroupTotalPoints}
                totalScaledPoints={isWeighted ? scaledPoints : undefined}
            />

            {isWeighted && (
                <>
                    {canEditForm ? (
                        <GroupScoreScalingFactorInput
                            questionGroupId={groupId}
                            label={t.labels.ScalingFactor}
                            labelPosition="beside"
                        />
                    ) : (
                        <GroupScoreScalingFactorViewer
                            questionGroupId={groupId}
                        />
                    )}
                </>
            )}

            <PercentageBar percentage={percentage} />
        </div>
    );
}
